import type { Theme } from '@emotion/react'
import { css } from '@emotion/react'

import {
  breakpoint,
  duration,
  easing,
} from '@twisto/styles'

export type Sizes = keyof ReturnType<typeof sizes>
export type Colors = keyof ReturnType<typeof colors>
export type Variants = keyof ReturnType<typeof variants>

export const root = (theme: Theme) => css`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  gap: ${theme.spacing[12]};
  user-select: none;
  transition: background ${duration.short}ms ${easing.easeIn};
  white-space: nowrap;

  &[disabled] {
    color: ${theme.palette.text.disabled};
    pointer-events: none;
  }
`

export const fullWidth = css`
  width: 100%;
`

export const sizes = (theme: Theme) => ({
  small: css`
    padding: ${theme.spacing[8]} ${theme.spacing[16]};
  `,
  medium: css`
    padding: ${theme.spacing[12]} ${theme.spacing[24]};
  `,
  large: css`
    padding: ${theme.spacing[12]} ${theme.spacing[24]};
  `,
})

export const colors = (theme: Theme) => ({
  contained: containedStyles(theme),
  primary: css`
    ${containedStyles(theme)}
  `,
  outlined: outlinedStyles(theme),
  secondary: css`
    ${outlinedStyles(theme)}
  `,
  negative: css`
    color: ${theme.palette.error.main};
    background: ${theme.palette.error.lowContrast};

    ${colorPseudoClasses({
      hoverBackgroundColor: theme.palette.error.mediumContrast,
      focusOutlineColor: theme.palette.error.mediumContrast,
    })}

    &[disabled] {
      background: ${theme.palette.disabled.lowContrast};
    }
  `,
  ghost: css`
    color: ${theme.palette.accent.highContrast};
    background: transparent;

    ${colorPseudoClasses({
      hoverColor: theme.palette.accent.maxContrast,
      focusOutlineColor: theme.palette.primary.lowContrast,
    })}

    &[disabled] {
      background: transparent;
    }
  `,
  link: css`
    color: ${theme.palette.primary.mainContrast};
    background: transparent;

    ${colorPseudoClasses({
      focusOutlineColor: theme.palette.primary.lowContrast,
    })}
  `,
  'reverse_contained': reversedContainedStyles(theme),
  'reverse_outlined': reversedOutlinedStyles(theme),
  'contained_cms': cmsContainedStyles(theme),
  'outlined_cms': cmsOutlinedStyles(theme),
})

export const variants = (theme: Theme) => ({
  default: containedStyles(theme),
  contained: containedStyles(theme),
  outlined: outlinedStyles(theme),
  negative: css`
    ${colors(theme).negative}
  `,
  ghost: css`
    ${colors(theme).ghost}
  `,
  'reverse_contained': reversedContainedStyles(theme),
  reversedPrimary: reversedContainedStyles(theme),
  'reverse_outlined': reversedOutlinedStyles(theme),
  reversedSecondary: reversedOutlinedStyles(theme),
  'contained_cms': cmsContainedStyles(theme),
  'outlined_cms': cmsOutlinedStyles(theme),
  inverted: reversedContainedStyles(theme),
  outlinedInverted: reversedOutlinedStyles(theme),
})

export const cmsSize = (theme: Theme, size: Sizes) => css`
  ${size === 'small'
    ? css`
        padding-block: ${theme.spacing[12]};
      `
    : undefined}

  ${size === 'large'
    ? css`
        padding-block: ${theme.spacing[16]};
      `
    : undefined}
`

export const reversedSize = (theme: Theme, size: Sizes) => css`
  ${breakpoint.up('md')} {
    ${cmsSize(theme, size)}
  }
`

export const typography = (
  theme: Theme,
  size: Sizes,
  isDesktop: boolean
) => {
  const typographyMap = {
    large: isDesktop ? theme.typography.app.body : theme.typography.app.bodyMobile,
    medium: isDesktop
      ? theme.typography.app.bodySm
      : theme.typography.app.bodySmMobile,
    small: isDesktop
      ? theme.typography.app.bodySm
      : theme.typography.app.bodySmMobile,
  } satisfies Record<Sizes, {
    fontSize: number
    fontWeight: number
    lineHeight: number | 'auto'
    letterSpacing: number
  }>

  const { fontSize, lineHeight, letterSpacing } = typographyMap[size]

  return css`
    display: inline-block;
    font-family: ${theme.typography.fontFamily}, sans-serif;
    font-size: ${fontSize}px;
    font-weight: ${theme.typography.fontWeight.medium};
    letter-spacing: ${letterSpacing}px;
    line-height: ${lineHeight === 'auto' ? 'normal' : `${lineHeight}px`};
  `
}

const containedStyles = (theme: Theme) => css`
    color: ${theme.palette.text.titleReversed};
    background: ${theme.palette.primary.highContrast};

    ${colorPseudoClasses({
      hoverBackgroundColor: theme.palette.primary.maxContrast,
      focusOutlineColor: theme.palette.primary.maxContrast,
    })}

    &[disabled] {
      background: ${theme.palette.disabled.lowContrast};
    }
`

const outlinedStyles = (theme: Theme) => css`
    color: ${theme.palette.primary.highContrast};
    background: ${theme.palette.background.main};
    border: 1px solid ${theme.palette.primary.midContrast};

    ${colorPseudoClasses({
      hoverBackgroundColor: theme.palette.primary.lowContrast,
      focusOutlineColor: theme.palette.primary.highContrast,
    })}

    &[disabled] {
      background: ${theme.palette.disabled.lowContrast};
      border-color: transparent;
    }
`

const reversedContainedStyles = (theme: Theme) => css`
    color: ${theme.palette.primary.highContrast};
    background: ${theme.palette.secondary.reversedContrast};
    box-shadow: ${theme.shadows.shadowSmall};

    ${colorPseudoClasses({
      hoverColor: theme.palette.text.titleReversed,
      hoverBackgroundColor: theme.palette.primary.maxContrast,
      focusOutlineColor: theme.palette.primary.maxContrast,
    })}
`

const reversedOutlinedStyles = (theme: Theme) => css`
    color: ${theme.palette.text.titleReversed};
    background: transparent;
    border: 1px solid ${theme.palette.secondary.reversedContrast};

    ${colorPseudoClasses({
      hoverColor: theme.palette.primary.reversedContrast,
      hoverBackgroundColor: theme.palette.primary.maxContrast,
      hoverBorderColor: 'transparent',
      focusOutlineColor: theme.palette.primary.maxContrast,
    })}
`

const cmsContainedStyles = (theme: Theme) => css`
    color: ${theme.palette.text.titleReversed};
    background: ${theme.palette.primary.highContrast};
`

const cmsOutlinedStyles = (theme: Theme) => css`
    color: ${theme.palette.primary.highContrast};
    background: transparent;
    border: 1px solid ${theme.palette.primary.midContrast};
`

const colorPseudoClasses = ({
  hoverBackgroundColor,
  hoverBorderColor,
  hoverColor,
  focusOutlineColor,
}: {
  hoverBackgroundColor?: string
  focusOutlineColor?: string
  hoverColor?: string
  hoverBorderColor?: string
}) => css`
  &:hover {
    background: ${hoverBackgroundColor};
    transition: background ${duration.short}ms ${easing.easeOut};
    color: ${hoverColor};
    border-color: ${hoverBorderColor};
  }

  &:focus-visible {
    outline: 2px solid ${focusOutlineColor};
    outline-offset: 2px;
    transition: background ${duration.short}ms ${easing.easeOut};
  }
`
